import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultFooterContent = {
  company_description: "Full-service general contracting firm delivering high-quality residential and commercial construction solutions throughout New Jersey.",
  phone: "862-350-7122",
  email: "estimating@astute-builders.com",
  location: "New Jersey",
  cta_title: "Ready to Start Your Project?",
  cta_description: "Book a free 30-minute consultation to discuss your project requirements with our team.",
  service_areas: {
    nj_title: "New Jersey",
    nj_areas: ["Newark • Jersey City • Hoboken", "Bergen County • Essex County", "Princeton • Trenton"],
    ny_title: "", 
    ny_areas: []
  }
};

const defaultServices = [
  "General Contracting",
  "Commercial Construction",
  "Residential Renovations",
  "Interior Build-Outs",
  "Project Management",
];

export const Footer = () => {
  const [content, setContent] = useState(defaultFooterContent);
  const [services, setServices] = useState(defaultServices);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [footerRes, servicesRes] = await Promise.all([
          axios.get(`${API}/site-content/footer`),
          axios.get(`${API}/site-content/services`)
        ]);
        
        if (footerRes.data?.content) {
          // Fully replace content with API data
          setContent(footerRes.data.content);
        }
        if (servicesRes.data?.content?.list) {
          setServices(servicesRes.data.content.list.slice(0, 5).map(s => s.title));
        }
      } catch (error) {
        console.error("Error fetching footer content:", error);
      } finally {
        setIsLoaded(true);
      }
    };
    fetchContent();
  }, []);

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Our Services", path: "/services" },
    { name: "Projects", path: "/projects" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
    { name: "Schedule Consultation", path: "/consultation" },
  ];

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#2A2A2A]" data-testid="footer">
      {/* CTA Section */}
      <div className="bg-[#121212] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
            {content.cta_title || "Ready to Start Your Project?"}
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
            {content.cta_description || "Book a free 30-minute consultation to discuss your project requirements with our team."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="footer-schedule-btn">
              <Calendar size={18} />
              Schedule a Consultation
            </Link>
            <a href={`tel:${content.phone || "862-350-7122"}`} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="footer-call-btn">
              <Phone size={18} />
              Call Now
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <div className="mb-6 flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/n0vcoewk_fulllogo.png" 
                  alt="Astute Builders Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6">
                {content.company_description || "Full-service general contracting firm delivering high-quality residential and commercial construction solutions throughout New Jersey."}
              </p>
              <div className="flex flex-col gap-3">
                <a href={`tel:${content.phone || "862-350-7122"}`} className="footer-link flex items-center gap-3 text-sm">
                  <Phone size={16} className="text-[#7ED4C6]" />
                  {content.phone || "862-350-7122"}
                </a>
                <a href={`mailto:${content.email || "estimating@astute-builders.com"}`} className="footer-link flex items-center gap-3 text-sm">
                  <Mail size={16} className="text-[#7ED4C6]" />
                  {content.email || "estimating@astute-builders.com"}
                </a>
                <div className="footer-link flex items-center gap-3 text-sm">
                  <MapPin size={16} className="text-[#7ED4C6]" />
                  {content.location || "New Jersey"}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-['Oswald'] text-lg font-semibold text-white mb-6 uppercase tracking-wider">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="footer-link text-sm flex items-center gap-2 group"
                    >
                      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#7ED4C6]" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-['Oswald'] text-lg font-semibold text-white mb-6 uppercase tracking-wider">
                Our Services
              </h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service}>
                    <Link
                      to="/services"
                      className="footer-link text-sm flex items-center gap-2 group"
                    >
                      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#7ED4C6]" />
                      {service}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Area */}
            <div>
              <h4 className="font-['Oswald'] text-lg font-semibold text-white mb-6 uppercase tracking-wider">
                Service Areas
              </h4>
              <div className="space-y-2 text-[#A1A1AA] text-sm">
                {content.service_areas?.nj_title && (
                  <>
                    <p className="font-semibold text-white">{content.service_areas.nj_title}</p>
                    {(content.service_areas?.nj_areas || []).map((area, idx) => (
                      <p key={idx}>{area}</p>
                    ))}
                  </>
                )}
                {content.service_areas?.ny_title && (
                  <>
                    <p className="font-semibold text-white mt-4">{content.service_areas.ny_title}</p>
                    {(content.service_areas?.ny_areas || []).map((area, idx) => (
                      <p key={idx}>{area}</p>
                    ))}
                  </>
                )}
                {/* Fallback if no content loaded */}
                {!content.service_areas?.nj_title && !content.service_areas?.ny_title && (
                  <>
                    <p className="font-semibold text-white">New Jersey</p>
                    {defaultFooterContent.service_areas.nj_areas.map((area, idx) => (
                      <p key={idx}>{area}</p>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A2A2A] py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#A1A1AA] text-sm">
            © {new Date().getFullYear()} Astute Builders LLC. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="footer-link">Privacy Policy</a>
            <a href="#" className="footer-link">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
